import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SendHorizontal, Trash2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { timeAgo } from "@/lib/timeago";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export const CommentSection = ({ targetType, targetId, onCountChange }) => {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get(`/comments/${targetType}/${targetId}`).then(({ data }) => setComments(data)).catch(() => {});
  }, [targetType, targetId]);

  useEffect(() => { load(); }, [load]);

  const canDelete = (c) => user && (c.user_id === user.id || ["super_admin", "association_admin", "moderator"].includes(user.role));

  const post = async (e) => {
    e.preventDefault();
    if (!user) return navigate("/login");
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/comments/${targetType}/${targetId}`, { content: text.trim() });
      const next = [...comments, data];
      setComments(next);
      setText("");
      onCountChange?.(next.length);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      const next = comments.filter((c) => c.id !== id);
      setComments(next);
      onCountChange?.(next.length);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="comment-section">
      <div className="space-y-3">
        {comments.map((c) => (
          <div key={c.id} data-testid="comment-item" className="flex gap-2.5 group">
            <Avatar className="h-7 w-7 shrink-0 border border-border">
              <AvatarImage src={c.user_picture} alt={c.user_name} />
              <AvatarFallback className="text-[10px] font-semibold">{c.user_name?.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="bg-muted rounded-2xl rounded-tl-sm px-3.5 py-2">
                <p className="text-xs font-semibold">{c.user_name}</p>
                <p className="text-sm mt-0.5 break-words">{c.content}</p>
              </div>
              <p className="text-[10px] font-mono-data text-muted-foreground mt-1 ml-1">{timeAgo(c.created_at, lang)}</p>
            </div>
            {canDelete(c) && (
              <button data-testid="delete-comment-btn" onClick={() => remove(c.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0 self-start mt-1.5">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      <form onSubmit={post} className="flex items-center gap-2 mt-3">
        <Input
          data-testid="comment-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={user ? t("write_comment") : t("login_required")}
          maxLength={1000}
          className="rounded-full h-9 text-sm"
          onFocus={() => { if (!user) navigate("/login"); }}
        />
        <Button type="submit" size="icon" data-testid="comment-submit-btn" disabled={posting || !text.trim()} className="rounded-full h-9 w-9 shrink-0 active:scale-95">
          <SendHorizontal className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
};
